"use client";

import {
    ConnectionProvider,
    WalletProvider,
} from "@solana/wallet-adapter-react";
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui";
import { SolflareWalletAdapter } from "@solana/wallet-adapter-solflare";
import {
    createContext,
    ReactElement,
    ReactNode,
    useContext,
    useMemo,
} from "react";
import { ParallaxProvider } from "react-scroll-parallax";
import { APIProvider } from "@vis.gl/react-google-maps";

import { EventFilterProvider } from "~/contexts/EventSearchContext";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";
import { TRPCReactProvider } from "~/trpc/react";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { SOLANA_RPC_ENDPOINT } from "~/utils/web3";
import { api } from "~/utils/api";
import { env } from "~/env.mjs";
import { GetEventFromSlug } from "./beta/events/[eventSlug]/actions";
import { GetCommunity } from "./v1/[communitySlug]/actions";
import { GetCommunityDetailsAndExtraDetails } from "~/utils/community-queries";
import { GetCommunityOverviewPeople } from "./beta/[communitySlug]/actions";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import {
    getWhitelabeledCommunityBy,
    getWhiteLabelSlug,
    WhitelabeledCommunity,
} from "~/server/api/helpers/communityHelpers";
import { usePathname } from "next/navigation";

export function Providers({ children }: { children: React.ReactNode }) {
    const endpoint = useMemo(() => SOLANA_RPC_ENDPOINT, []);
    const wallets = useMemo(() => [new SolflareWalletAdapter()], []);
    const pathname = usePathname();
    const googleMapsApiKey = env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    const hostname =
        typeof window !== "undefined" ? window.location.hostname : undefined;

    const whiteLabelProps = useMemo(() => {
        return getWhitelabeledCommunityBy(
            "slug",
            getWhiteLabelSlug(pathname, hostname),
        );
    }, [pathname, hostname]);

    return (
        <TRPCReactProvider>
            <EventFilterProvider>
                <ParallaxProvider>
                    <WhiteLabelContextContextProvider
                        customization={whiteLabelProps?.layout}
                        whiteLabelProps={whiteLabelProps}
                    >
                        <ConnectionProvider endpoint={endpoint}>
                            <WalletProvider wallets={wallets} autoConnect>
                                <WalletModalProvider>
                                    <APIProvider apiKey={googleMapsApiKey}>
                                        {children}
                                    </APIProvider>
                                </WalletModalProvider>
                            </WalletProvider>
                        </ConnectionProvider>
                    </WhiteLabelContextContextProvider>
                </ParallaxProvider>
            </EventFilterProvider>
        </TRPCReactProvider>
    );
}

const PermissionsContext = createContext<UserPermissions | undefined>(
    undefined,
);

export function PermissionsContextProvider({
    children,
    permissions,
}: {
    children: ReactNode;
    permissions: UserPermissions;
}) {
    return (
        <PermissionsContext.Provider value={permissions}>
            {children}
        </PermissionsContext.Provider>
    );
}

export const usePermissionsContext = () => {
    const context = useContext(PermissionsContext);
    if (context === undefined) {
        throw new Error(
            "usePermissionsContext must be used within an PermissionsContextProvider",
        );
    }
    return context;
};

const WhiteLabelContext = createContext<
    | {
          customization: CustomizableLayout | undefined;
          whiteLabelProps: WhitelabeledCommunity | undefined;
      }
    | undefined
>(undefined);

export function WhiteLabelContextContextProvider({
    children,
    customization,
    whiteLabelProps,
}: {
    children: ReactNode;
    customization: CustomizableLayout | undefined;
    whiteLabelProps: WhitelabeledCommunity | undefined;
}) {
    return (
        <WhiteLabelContext.Provider value={{ customization, whiteLabelProps }}>
            {children}
        </WhiteLabelContext.Provider>
    );
}

export const useWhiteLabel = () => {
    const context = useContext(WhiteLabelContext);
    return context;
};

const EventContext = createContext<
    { event: EventBySlugOutput; refetch: () => void } | undefined
>(undefined);

export function EventContextProvider({
    children,
    event: eventData,
}: {
    children: ReactNode;
    event: EventBySlugOutput;
}) {
    const { data: event, refetch: refetch } = api.event.getBySlug.useQuery(
        eventData.slug,
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            initialData: eventData,
        },
    );

    return (
        <EventContext.Provider value={{ event, refetch }}>
            {children}
        </EventContext.Provider>
    );
}

export const useEventContext = () => {
    const context = useContext(EventContext);
    if (context === undefined) {
        throw new Error(
            "useEventContext must be used within an EventContextProvider",
        );
    }
    return context;
};

const EventContextV2 = createContext<GetEventFromSlug | undefined>(undefined);

export function EventContextProviderV2({
    children,
    data,
}: {
    children: ReactNode;
    data: GetEventFromSlug;
}) {
    return (
        <EventContextV2.Provider value={data}>
            {children}
        </EventContextV2.Provider>
    );
}

export const useEventContextV2 = (): GetEventFromSlug => {
    const context = useContext(EventContextV2);
    if (context === undefined) {
        throw new Error(
            "useEventContextV2 must be used within an EventContextProviderV2",
        );
    }
    return context;
};

const EventRSVPWrapperContext = createContext<ReactElement | undefined>(
    undefined,
);

const CommunityContext = createContext<
    | {
          community?: GetCommunity;
          communityWithAllDetails?: GetCommunityDetailsAndExtraDetails;
          communityPeople?: GetCommunityOverviewPeople;
      }
    | undefined
>(undefined);
export const CommunityContextProvider = ({
    children,
    data,
}: {
    children: ReactNode;
    data?: {
        community?: GetCommunity;
        communityWithAllDetails: GetCommunityDetailsAndExtraDetails;
        communityPeople: GetCommunityOverviewPeople;
    };
}) => {
    return (
        <CommunityContext.Provider value={{ ...data }}>
            {children}
        </CommunityContext.Provider>
    );
};
export const useCommunityContext = () => {
    const context = useContext(CommunityContext);
    if (context === undefined) {
        throw new Error(
            "useCommunityContext must be used within an CommunityContextProvider",
        );
    }
    return context;
};

export const EventRSVPWrapperProvider = ({
    component,
    children,
}: {
    component: ReactElement;
    children: ReactNode;
}) => {
    const memoizedComponent = useMemo(() => component, [component]);
    return (
        <EventRSVPWrapperContext.Provider value={memoizedComponent}>
            {children}
        </EventRSVPWrapperContext.Provider>
    );
};

export const useEventRSVPWrapper = () => {
    return useContext(EventRSVPWrapperContext);
};
